import React, { useState, useEffect } from "react";
import { Table, Typography, Card, Spin } from "antd";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import { Dropdown, ButtonGroup, Button } from "react-bootstrap";
import { FaCalendarAlt } from "react-icons/fa";
import AccountingReportTabs from "./AccoutingReportTabs";
import Header from "../../Common/Layout/Header";
import Login from "../../Authentication/Login";
import { useNavigate } from "react-router-dom";

const { Text, Title } = Typography;

const JournalReport = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(moment().startOf("month").toDate());
  const [toDate, setToDate] = useState(moment().endOf("month").toDate());
  const [companyData, setCompanyData] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");

  // Get user's currency from auth context
  const getUserCurrency = () => {
    return user?.currency || "INR"; // Fallback to INR if currency is not available
  };

  // Get branches for the dropdown
  const GetBranches = async () => {
    try {
      const res = await apiService.getAll(
        `${config.GETBRANCHBYCOMPANY}/${user?.cid}`
      );
      setBranches(res.data || res);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const GetCompanyData = async () => {
    try {
      const res = await apiService.get(`${config.GETCOMPANYBYUSER}/${user.id}`);
      setCompanyData(res);
    } catch (err) {
      console.error("Error fetching company data:", err);
    }
  };

  const GetJournalEntries = async () => {
    setLoadingReport(true);
    try {
      const params = new URLSearchParams();
      params.append("userId", user.id);
      if (selectedBranch && selectedBranch !== "all") {
        params.append("branchId", selectedBranch);
      }
      if (fromDate)
        params.append("FromDate", moment(fromDate).format("YYYY-MM-DD"));
      if (toDate) params.append("ToDate", moment(toDate).format("YYYY-MM-DD"));

      const res = await apiService.getAll(
        `${config.JOURNALENTRIESREPORT}/${user.id}?${params.toString()}`
      );

      const groupedData = res.reduce((acc, entry) => {
        if (!acc[entry.invoiceNo]) {
          acc[entry.invoiceNo] = {
            invoiceDate: entry.invoiceDate,
            invoiceNo: entry.invoiceNo,
            entries: [],
          };
        }
        acc[entry.invoiceNo].entries.push(entry);
        return acc;
      }, {});

      setReportData(Object.values(groupedData));
    } catch (err) {
      console.error("Error fetching journal entries:", err);
    } finally {
      setLoadingReport(false);
    }
  };

  // useEffect(() => {
  //   if (user && !loading) {
  //     GetCompanyData();
  //     GetBranches();
  //     GetJournalEntries();
  //   }
  // }, [user, loading]);

  useEffect(() => {
    if (user && !loading) {
      GetCompanyData();
      GetBranches();
      GetJournalEntries();
    }
  }, [user, loading, fromDate, toDate, selectedBranch]);

  const handleSearch = (e) => {
    e.preventDefault();
    GetJournalEntries();
  };

  const handleClear = () => {
    setFromDate(moment().startOf("month").toDate());
    setToDate(moment().endOf("month").toDate());
    setSelectedBranch("all");
    setReportData([]);
  };

  const exportToPDF = () => {
    const currency = getUserCurrency();
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    doc.setFont("helvetica");
    doc.setFontSize(16);
    doc.setTextColor(44, 62, 80);
    doc.setFont(undefined, "bold");
    doc.text("GENERAL JOURNAL", 105, 20, { align: "center" });

    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.setFont(undefined, "normal");
    doc.text(companyData?.companyName || "Company", 105, 30, {
      align: "center",
    });

    // Branch information if selected
    if (selectedBranch && selectedBranch !== "all") {
      const branchName =
        branches.find((b) => b.branchId === selectedBranch)?.branchName ||
        selectedBranch;
      doc.text(`Branch: ${branchName}`, 105, 35, { align: "center" });
    }

    // Address
    const addressLine = `${companyData?.address || ""}, ${
      companyData?.city || ""
    }, ${companyData?.state || ""}, ${companyData?.country || ""}`;
    doc.text(
      addressLine,
      105,
      selectedBranch && selectedBranch !== "all" ? 40 : 35,
      {
        align: "center",
      }
    );

    // Contact info
    const contactLine = `Phone: ${companyData?.phoneNumber || ""} | Email: ${
      companyData?.email || ""
    }`;
    doc.text(
      contactLine,
      105,
      selectedBranch && selectedBranch !== "all" ? 45 : 40,
      {
        align: "center",
      }
    );

    doc.text(
      `For the period from ${moment(fromDate).format("YYYY-MM-DD")} to ${moment(
        toDate
      ).format("YYYY-MM-DD")}`,
      105,
      selectedBranch && selectedBranch !== "all" ? 50 : 45,
      { align: "center" }
    );

    doc.text(
      `Generated on: ${moment().format("YYYY-MM-DD hh:mm A")}`,
      105,
      selectedBranch && selectedBranch !== "all" ? 55 : 50,
      { align: "center" }
    );

    const tableData = [];
    reportData.forEach((group) => {
      group.entries.forEach((entry, index) => {
        tableData.push([
          index === 0 ? moment(group.invoiceDate).format("MM/DD/YYYY") : "",
          index === 0 ? group.invoiceNo : "",
          entry.account,
          entry.debit ? `${currency} ${entry.debit.toFixed(2)}` : "",
          entry.credit ? `${currency} ${entry.credit.toFixed(2)}` : "",
        ]);
      });
    });

    doc.autoTable({
      startY: selectedBranch && selectedBranch !== "all" ? 65 : 60,
      head: [
        [
          "Date",
          "Invoice No",
          "Account",
          `Debit (${currency})`,
          `Credit (${currency})`,
        ],
      ],
      body: tableData,
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 25 },
        2: { cellWidth: 60 },
        3: { cellWidth: 25, halign: "right" },
        4: { cellWidth: 25, halign: "right" },
      },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
      },
    });

    const totalDebit = reportData.reduce(
      (sum, group) =>
        sum + group.entries.reduce((s, e) => s + (e.debit || 0), 0),
      0
    );
    const totalCredit = reportData.reduce(
      (sum, group) =>
        sum + group.entries.reduce((s, e) => s + (e.credit || 0), 0),
      0
    );

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 10,
      body: [
        [
          { content: "Totals:", styles: { fontStyle: "bold" } },
          "",
          "",
          {
            content: `${currency} ${totalDebit.toFixed(2)}`,
            styles: { fontStyle: "bold", halign: "right" },
          },
          {
            content: `${currency} ${totalCredit.toFixed(2)}`,
            styles: { fontStyle: "bold", halign: "right" },
          },
        ],
      ],
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 25 },
        2: { cellWidth: 60 },
        3: { cellWidth: 25, halign: "right" },
        4: { cellWidth: 25, halign: "right" },
      },
      styles: {
        fontSize: 9,
        cellPadding: 2,
      },
    });

    doc.save("Journal_Report.pdf");
  };

  const handlePrint = () => {
    const currency = getUserCurrency();
    const printWindow = window.open("", "_blank");
    const branchName =
      selectedBranch && selectedBranch !== "all"
        ? branches.find((b) => b.branchId === selectedBranch)?.branchName ||
          selectedBranch
        : null;

    const tableRows = [];
    reportData.forEach((group) => {
      group.entries.forEach((entry, index) => {
        tableRows.push(`
          <tr>
            <td>${
              index === 0 ? moment(group.invoiceDate).format("MM/DD/YYYY") : ""
            }</td>
            <td>${index === 0 ? group.invoiceNo : ""}</td>
            <td>${entry.account}</td>
            <td class="text-right">${
              entry.debit ? `${currency} ${entry.debit.toFixed(2)}` : ""
            }</td>
            <td class="text-right">${
              entry.credit ? `${currency} ${entry.credit.toFixed(2)}` : ""
            }</td>
          </tr>
        `);
      });
    });

    printWindow.document.write(`
      <html>
        <head>
          <title>General Journal</title>
          <style>
            body { font-family: Arial; margin: 0; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .company-name { font-size: 18px; font-weight: bold; }
            .company-info { font-size: 12px; margin-bottom: 5px; }
            .report-title { font-size: 16px; font-weight: bold; margin: 10px 0; }
            .report-period { font-size: 12px; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th { background-color: #2980b9; color: white; padding: 6px; text-align: left; }
            td { padding: 5px; border-bottom: 1px solid #ddd; }
            .text-right { text-align: right; }
            .total-row { font-weight: bold; }
            .footer { margin-top: 20px; font-size: 10px; text-align: center; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-info">
            <p>${companyData?.companyName || "Company"}</p>
            ${branchName ? `<p><strong>Branch:</strong> ${branchName}</p>` : ""}
            <p>${companyData?.address || ""}, ${companyData?.city || ""}, ${
      companyData?.state || ""
    }, ${companyData?.country || ""}</p>
            <p>Phone: ${companyData?.phoneNumber || ""} | Email: ${
      companyData?.email || ""
    }</p>
          </div>
            <div class="report-title">GENERAL JOURNAL</div>
            <div class="report-period">
              ${moment(fromDate).format("MM/DD/YYYY")} - ${moment(
      toDate
    ).format("MM/DD/YYYY")}<br>
              Generated on: ${moment().format("MM/DD/YYYY hh:mm A")}
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice No</th>
                <th>Account</th>
                <th class="text-right">Debit (${currency})</th>
                <th class="text-right">Credit (${currency})</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows.join("")}
              <tr class="total-row">
                <td colspan="3">Totals</td>
                <td class="text-right">${currency} ${reportData
      .reduce(
        (sum, group) =>
          sum + group.entries.reduce((s, e) => s + (e.debit || 0), 0),
        0
      )
      .toFixed(2)}</td>
                <td class="text-right">${currency} ${reportData
      .reduce(
        (sum, group) =>
          sum + group.entries.reduce((s, e) => s + (e.credit || 0), 0),
        0
      )
      .toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <div class="footer">
            © ${new Date().getFullYear()} ${
      companyData?.companyName || "Company"
    }. All rights reserved.
          </div>

          <script>
            setTimeout(() => {
              window.print();
              window.close();
            }, 200);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) return <p>Loading...</p>;

  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <AccountingReportTabs pageName="JOURNALREPORT" />
                </div>
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <div className="d-flex align-items-end flex-wrap">
                        <div className="date-picker-group me-2 mb-2">
                          <label className="form-label">Branch:</label>
                          <select
                            className="form-select"
                            value={selectedBranch}
                            onChange={(e) => setSelectedBranch(e.target.value)}
                          >
                            <option value="all">All Branches</option>
                            {branches.map((branch) => (
                              <option
                                key={branch.branchId}
                                value={branch.branchId}
                              >
                                {branch.branchName}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="date-picker-group me-2 mb-2">
                          <label className="form-label">From Date:</label>
                          <div className="position-relative">
                            <DatePicker
                              selected={fromDate}
                              onChange={(date) => setFromDate(date)}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control pe-4"
                              placeholderText="Select from date"
                              showIcon
                              icon={
                                <FaCalendarAlt className="position-absolute end-0 top-50 translate-middle-y me-2" />
                              }
                            />
                          </div>
                        </div>
                        <div className="date-picker-group me-2 mb-2">
                          <label className="form-label">To Date:</label>
                          <div className="position-relative">
                            <DatePicker
                              selected={toDate}
                              onChange={(date) => setToDate(date)}
                              minDate={fromDate}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control pe-4"
                              placeholderText="Select to date"
                              showIcon
                              icon={
                                <FaCalendarAlt className="position-absolute end-0 top-50 translate-middle-y me-2" />
                              }
                            />
                          </div>
                        </div>
                        <div className="d-flex mb-2">
                          <Button
                            variant="primary"
                            className="me-2"
                            onClick={handleSearch}
                          >
                            Search
                          </Button>
                          <Button variant="danger" onClick={handleClear}>
                            Clear
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 d-flex justify-content-end">
                      <Dropdown as={ButtonGroup}>
                        <Dropdown.Toggle variant="primary" id="dropdown-export">
                          <i className="ri-download-line me-1"></i>
                          Export
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={exportToPDF}>
                            <i className="ri-file-pdf-line me-2 text-danger"></i>
                            PDF
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      <Button
                        variant="secondary"
                        onClick={handlePrint}
                        className="ms-2"
                      >
                        <i className="ri-printer-line me-1"></i>
                        Print
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="report-container">
                    <div className="header-section">
                      <h1 className="company-name">
                        {companyData?.companyName || "Company"}
                      </h1>
                      {selectedBranch && selectedBranch !== "all" && (
                        <p className="branch-info">
                          <strong>Branch:</strong>{" "}
                          {branches.find((b) => b.branchId === selectedBranch)
                            ?.branchName || selectedBranch}
                        </p>
                      )}
                      <p className="company-info">
                        {companyData?.address || ""}, {companyData?.city || ""},{" "}
                        {companyData?.state || ""}, {companyData?.country || ""}
                        <br />
                        Phone: {companyData?.phoneNumber || ""} | Email:{" "}
                        {companyData?.email || ""}
                      </p>
                    </div>
                    <div className="report-title">
                      <h2>General Journal</h2>
                      <p className="report-period">
                        For the period from{" "}
                        {moment(fromDate).format("YYYY-MM-DD")} to{" "}
                        {moment(toDate).format("YYYY-MM-DD")}
                      </p>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-bordered table-hover mb-0">
                        <thead className="bg-soft-primary bg-gradient">
                          <tr>
                            <th>Date</th>
                            <th>Invoice No</th>
                            <th>Account</th>
                            <th className="text-end">
                              Debit ({getUserCurrency()})
                            </th>
                            <th className="text-end">
                              Credit ({getUserCurrency()})
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.length > 0 ? (
                            reportData.flatMap((group, groupIndex) =>
                              group.entries.map((entry, entryIndex) => (
                                <tr key={`entry-${groupIndex}-${entryIndex}`}>
                                  <td>
                                    {entryIndex === 0
                                      ? moment(group.invoiceDate).format(
                                          "YYYY-MM-DD"
                                        )
                                      : ""}
                                  </td>
                                  <td>
                                    {entryIndex === 0 ? group.invoiceNo : ""}
                                  </td>
                                  <td>{entry.account}</td>
                                  <td className="text-end">
                                    {entry.debit
                                      ? `${getUserCurrency()} ${entry.debit.toFixed(
                                          2
                                        )}`
                                      : ""}
                                  </td>
                                  <td className="text-end">
                                    {entry.credit
                                      ? `${getUserCurrency()} ${entry.credit.toFixed(
                                          2
                                        )}`
                                      : ""}
                                  </td>
                                </tr>
                              ))
                            )
                          ) : (
                            <tr>
                              <td
                                colSpan="5"
                                className="text-center text-muted"
                              >
                                No journal entries found
                              </td>
                            </tr>
                          )}
                          <tr className="total-row">
                            <td colSpan="3">Totals</td>
                            <td className="text-end">
                              {getUserCurrency()}{" "}
                              {reportData
                                .reduce(
                                  (sum, group) =>
                                    sum +
                                    group.entries.reduce(
                                      (s, e) => s + (e.debit || 0),
                                      0
                                    ),
                                  0
                                )
                                .toFixed(2)}
                            </td>
                            <td className="text-end">
                              {getUserCurrency()}{" "}
                              {reportData
                                .reduce(
                                  (sum, group) =>
                                    sum +
                                    group.entries.reduce(
                                      (s, e) => s + (e.credit || 0),
                                      0
                                    ),
                                  0
                                )
                                .toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="report-footer">
                      <p>
                        This report was generated automatically on{" "}
                        {moment().format("YYYY-MM-DD")}.
                      </p>
                      <p>
                        © {new Date().getFullYear()}{" "}
                        {companyData?.companyName || "Company"}. All rights
                        reserved.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JournalReport;
